import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogHeader,
  DialogBody,
  Button,
  Spinner,
} from "@material-tailwind/react";

// combobox
import { Combobox } from "@headlessui/react";

import NewCategory from "./New_category";
import NewDepot from "./New_depot";
import NewUnit from "views/pages/Products_list/components/New_unit";

import { getAllProductsCategoriesApi } from "api/filtersData";
import { getAllDepotsApi } from "api/depot";
import { getAllUnitsApi } from "api/unit";
import { getAllFournisseursApi } from "api/fournisseur";
import {
  addNewProductEssayApi as addNewProductApi,
  getOneProductEssayApi as getOneProductApi,
  updateProductEssayApi as updateProductApi,
  updateSingleDepotStockEssayApi as updateSingleDepotStockApi,
  getAllNonCounterDepotsEssay as getAllNonCounterDepots,
} from "api/productsEssay";

import { ToastContainer, toast } from "react-toastify";
import { getAllStockEntryByLotNumberEssayApi as getAllStockEntryByLotNumberApi } from "api/stockEntryEssay";

function ProductForm({ closePopup, clienFormAction, showToast, productID }) {
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showDepotModal, setShowDepotModal] = useState(false);
  const [showUnitModal, setShowUnitModal] = useState(false);

  const [productsCategories, setProductsCategories] = useState([]);
  const [productUnits, setProductUnits] = useState([]);
  const [fournisseurs, setFournisseurs] = useState([]);
  const [fournisseurQuery, setFournisseurQuery] = useState("");

  const [productDepot, setProductDepot] = useState([]);
  const [originalProductDepot, setOriginalProductDepot] = useState([]);
  const [lotNumbers, setLotNumbers] = useState([]);
  const [lotNumbersData, setLotNumbersData] = useState([]); // Store full stock entry data for sorting
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); // Loading state for form submission
  const [productFormData, setProductFormData] = useState({
    Code: "",
    Name: "",
    Unity: "KG",
    Packaging: "",
    isActive: true,
    Price: 0,
    HaveTVA: 0,
    sumOfStock: 0,
    PriceTTC: 0,
    Remise: 0,
    Categorie: "",
    CategorieID: "",
    CodeProduitComptable: "",
    FOURNISSEUR: "",
    FournisseurID: "",
    ExperationDate: "",
    LotNumber: [],
    selectedLotNumber: "all",
    AlertStock: 0,
    Bonus: 0
  });

  const filteredFournisseurs =
    fournisseurQuery === ""
      ? fournisseurs
      : fournisseurs.filter((el) =>
          el.Name.toLowerCase()
            .replace(/\s+/g, "")
            .includes(fournisseurQuery.toLowerCase().replace(/\s+/g, ""))
        );

  const formatLotExpirationDate = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (isNaN(d.getTime())) return "";
    const day = `${d.getDate()}`.padStart(2, "0");
    const month = `${d.getMonth() + 1}`.padStart(2, "0");
    const year = `${d.getFullYear()}`.toString().slice(-2);
    return `${day}/${month}/${year}`;
  };

  useEffect(() => {
    const loadInitialData = async () => {
      setIsLoading(true);
      try {
        await Promise.all([
          getAllProductsCategories(""),
          getAllProductUnits(""),
          getAllFournisseurs(""),
          getAllDepos("", ""),
          getProductDetails()
        ]);
      } finally {
        setIsLoading(false);
      }
    };
    loadInitialData();
  }, [productID]);

  useEffect(() => {
    // Fetch lot numbers when product code is available (view & edit modes)
    const shouldLoadLotNumbers =
      productFormData.Code && (clienFormAction === "edite" || clienFormAction === "show");

    if (shouldLoadLotNumbers) {
      const fetchLotNumbers = async () => {
        setIsLoading(true);
        try {
          const lotNumbersRes = await getAllStockEntryByLotNumberApi(productFormData.Code);
          console.log("lotNumbersRes: ", lotNumbersRes)

          // Sort by ExperationDate (ascending - earliest expiration first)
          const sortedLotNumbersRes = [...lotNumbersRes].sort((a, b) => {
            // Handle null/undefined expiration dates
            if (!a.ExperationDate && !b.ExperationDate) return 0;
            if (!a.ExperationDate) return 1; // a goes to end
            if (!b.ExperationDate) return -1; // b goes to end
            
            // Convert to Date objects and compare
            const dateA = new Date(a.ExperationDate);
            const dateB = new Date(b.ExperationDate);
            return dateA - dateB; // Ascending order (earliest first)
          });
          
          // Store full data for later use
          setLotNumbersData(sortedLotNumbersRes);
          
          // Extract sorted lot numbers
          const lotNumbersList = sortedLotNumbersRes.map(el => el.LotNumber);
          setLotNumbers(lotNumbersList);
          
          // If "all" is selected, populate LotNumber array with all lot numbers
          if (productFormData.selectedLotNumber === "all") {
            setProductFormData(prevState => ({
              ...prevState,
              LotNumber: [...lotNumbersList]
            }));
          }
        } catch (err) {
          console.log(err);
          setLotNumbers([]);
          setLotNumbersData([]);
        } finally {
          setIsLoading(false);
        }
      };
      fetchLotNumbers();
    }
  }, [productFormData.Code, clienFormAction]);

  // Populate LotNumber array when "all" is selected and lot numbers are loaded
  useEffect(() => {
    if (productFormData.selectedLotNumber === "all" && lotNumbers.length > 0) {
      const currentLotNumberSet = new Set(productFormData.LotNumber);
      const allLotNumbersSet = new Set(lotNumbers);
      
      // Only update if LotNumber doesn't already contain all lot numbers
      if (currentLotNumberSet.size !== allLotNumbersSet.size || 
          !lotNumbers.every(lot => currentLotNumberSet.has(lot))) {
        setProductFormData(prevState => ({
          ...prevState,
          LotNumber: [...lotNumbers]
        }));
      }
    }
  }, [lotNumbers, productFormData.selectedLotNumber]);

  // Populate LotNumber array when "all" is selected in edit mode and lot numbers are loaded
  useEffect(() => {
    const isViewOrEdit = clienFormAction === "edite" || clienFormAction === "show";
    if (isViewOrEdit && productFormData.selectedLotNumber === "all" && lotNumbers.length > 0) {
      setProductFormData(prevState => ({
        ...prevState,
        LotNumber: [...lotNumbers]
      }));
    }
  }, [lotNumbers, productFormData.selectedLotNumber, clienFormAction]);

  useEffect(() => {
    setProductFormData((prevState) => ({
      ...prevState,
      // PriceTTC: parseFloat(prevState.PriceTTC) + (parseFloat(prevState.PriceTTC) * (prevState.PriceTTC(prevState.HaveTVA) / 100))
      PriceTTC: parseFloat(prevState.Price) + parseFloat(prevState.Price) * (parseFloat(prevState.HaveTVA) / 100),
    }));
  }, [productFormData.Price, productFormData.HaveTVA]);

  const getProductDetails = async () => {
    try {
      if (clienFormAction !== "add") {
        console.log(productID);
        const productDtails = await getOneProductApi(productID);

        setProductFormData({
          Code: productDtails.Code,
          Name: productDtails.Name,
          Unity: productDtails.Unity,
          Packaging: productDtails.Packaging,
          isActive: productDtails.isActive,
          Price: productDtails.Price,
          HaveTVA: productDtails.HaveTVA,
          sumOfStock: productDtails.sumOfStock,
          PriceTTC: productDtails.PriceTTC,
          Remise: productDtails.Remise,
          Categorie: productDtails.Categorie,
          CategorieID: productDtails.CategorieID,
          CodeProduitComptable: productDtails.CodeProduitComptable,
          FOURNISSEUR: productDtails.FOURNISSEUR || "",
          FournisseurID: productDtails.FournisseurID || "",
          ExperationDate: productDtails.ExperationDate,
          LotNumber: productDtails.LotNumber || [],
          selectedLotNumber: "all",
          Stock: productDtails.Stock,
          Disponible: productDtails.Disponible,
          AlertStock: productDtails.AlertStock || 0,
          Bonus: productDtails.Bonus || 0,
        });

        // Store original depot values for comparison
        const originalStock = productDtails.Stock ? productDtails.Stock.map(depot => ({
          ...depot,
          Disponible: parseFloat(depot.Disponible || 0)
        })) : [];
        setOriginalProductDepot(originalStock);
        setProductDepot(productDtails.Stock);
      }
    } catch (err) {
      console.log(err);
    }
    return Promise.resolve();
  };

  const getAllFournisseurs = async (Name) => {
    try {
      const fournisseursList = await getAllFournisseursApi(Name);
      setFournisseurs(fournisseursList);
    } catch (err) {
      console.log(err);
    }
    return Promise.resolve();
  };

  const handleSelectedFournisseur = (fournisseurID) => {
    try {
      const selectedFournisseur = fournisseurs.find((el) => el._id === fournisseurID);
      setProductFormData((prevState) => ({
        ...prevState,
        FournisseurID: fournisseurID || "",
        FOURNISSEUR: selectedFournisseur ? selectedFournisseur.Name : "",
      }));
    } catch (err) {
      console.log(err);
    }
  };

  const getFournisseurDisplayName = (fournisseurID) => {
    const selectedFournisseur = fournisseurs.find((el) => el._id === fournisseurID);
    if (selectedFournisseur) return selectedFournisseur.Name;
    // Legacy products may have a free-text FOURNISSEUR name with no linked _id yet
    return fournisseurID ? "" : productFormData.FOURNISSEUR || "";
  };

  const handleInputChange = async (e) => {
    try {
      const { name, value } = e.target;
    
      // If product code changes, fetch lot numbers
      // if (name === "Code" && value) {
      //   setIsLoading(true);
      //   try {
      //     const lotNumbersRes = await getAllStockEntryByLotNumberApi(value);
      //     const lotNumbersList = lotNumbersRes.map(el => el.LotNumber);
      //     setLotNumbers(lotNumbersList);
          
      //     // If "all" is already selected, populate LotNumber array with all lot numbers
      //     if (productFormData.selectedLotNumber === "all") {
      //       setProductFormData(prevState => ({
      //         ...prevState,
      //         LotNumber: [...lotNumbersList]
      //       }));
      //     }
      //   } catch (err) {
      //     console.log(err);
      //     setLotNumbers([]);
      //   } finally {
      //     setIsLoading(false);
      //   }
      // }
      
      // Handle lot number selection
      if (name === "selectedLotNumber") {
        // Use already sorted lotNumbersData if available, otherwise fetch and sort
        let productLotNumbers = lotNumbersData;
        if (!productLotNumbers || productLotNumbers.length === 0) {
          const fetchedLotNumbers = await getAllStockEntryByLotNumberApi(productFormData.Code);
          // Sort by ExperationDate (ascending - earliest expiration first)
          productLotNumbers = [...fetchedLotNumbers].sort((a, b) => {
            // Handle null/undefined expiration dates
            if (!a.ExperationDate && !b.ExperationDate) return 0;
            if (!a.ExperationDate) return 1; // a goes to end
            if (!b.ExperationDate) return -1; // b goes to end
            
            // Convert to Date objects and compare
            const dateA = new Date(a.ExperationDate);
            const dateB = new Date(b.ExperationDate);
            return dateA - dateB; // Ascending order (earliest first)
          });
          setLotNumbersData(productLotNumbers);
        }

        if(value !== "all") {
          const selectedLotNumber = productLotNumbers.find(el => el.LotNumber === value)
          const stockDetails = selectedLotNumber.ProductDetails[0].Stock
          
          if(stockDetails) {
            // Store original values for comparison
            const originalStock = stockDetails.map(depot => ({
              ...depot,
              Disponible: parseFloat(depot.Disponible || 0)
            }));
            setOriginalProductDepot(originalStock);
            setProductDepot(stockDetails);
          }
        }
        else {
          const productDtails = await getOneProductApi(productID);
          // Store original values for comparison
          const originalStock = productDtails.Stock ? productDtails.Stock.map(depot => ({
            ...depot,
            Disponible: parseFloat(depot.Disponible || 0)
          })) : [];
          setOriginalProductDepot(originalStock);
          setProductDepot(productDtails.Stock);
        }
  
        if (value === "all") {
          // Select all lot numbers
          setProductFormData(prevState => ({
            ...prevState,
            selectedLotNumber: "all",
            LotNumber: [...lotNumbers]
          }));
        } else {
          // Select single lot number
          setProductFormData(prevState => ({
            ...prevState,
            selectedLotNumber: value,
            LotNumber: value ? [value] : []
          }));
        }
        return;
      }

      // Update product form data for regular inputs
      setProductFormData(prevState => {
        const newState = { ...prevState, [name]: value };
        // Also sync Categorie name when CategorieID changes
        if (name === "CategorieID") {
          const selectedCategory = productsCategories.find((el) => el._id === value);
          newState.Categorie = selectedCategory ? selectedCategory.Name : "";
        }
        return newState;
      });
    }
    catch (err) {
      console.log(err)
    }
    finally {
      setIsLoading(false);
    }
  };

  const getAllProductsCategories = async (categoryName) => {
    try {
      const productsCategories = await getAllProductsCategoriesApi(
        categoryName
      );
      setProductsCategories(productsCategories);
    } catch (err) {
      console.log(err);
    }
    return Promise.resolve();
  };

  const getAllDepos = async (Name, Origin) => {
    try {
      const depotsList = await getAllDepotsApi(Name, Origin);
      const listOfDepot = [];

      depotsList.map((el) => {
        listOfDepot.push({
          City: el.Name,
          Disponible: 0,
          fromDepot: el._id,
        });
      });

      setProductDepot(listOfDepot);
    } catch (err) {
      console.log(err);
    }
    return Promise.resolve();
  };

  const getAllProductUnits = async (unitName) => {
    try {
      const units = await getAllUnitsApi(unitName);
      setProductUnits(Array.isArray(units) ? units : []);
    } catch (err) {
      console.log(err);
    }
    return Promise.resolve();
  };

  const handleNewUnitModal = (action) => {
    try {
      if (action === "Reload") getAllProductUnits("");

      setShowUnitModal(!showUnitModal);
    } catch (err) {
      console.log(err);
    }
  };

  const handleNewCategoryModal = (action) => {
    try {
      if (action === "Reload") getAllProductsCategories("");

      setShowCategoryModal(!showCategoryModal);
    } catch (err) {
      console.log(err);
    }
  };
  const handleNewDepotModal = (action) => {
    try {
      if (action === "Reload") getAllDepos("", "");

      setShowDepotModal(!showDepotModal);
    } catch (err) {
      console.log(err);
    }
  };

  const handleCancelOp = () => {
    try {
      closePopup("Close");
    } catch (err) {}
  };

  const handleAddNewProduct = async () => {
    if (isSubmitting) return; // Prevent duplicate submissions
    
    try {
      setIsSubmitting(true);
      
      const selectedCategory = productsCategories.find(
        (el) => el._id === productFormData?.CategorieID
      );

      const productObject = {
        ...productFormData,
        Stock: productDepot,
        Disponible: 0,
        Categorie: selectedCategory?.Name,
        // Ensure AlertStock and Bonus are strings to match model type
        AlertStock: productFormData.AlertStock !== undefined ? String(productFormData.AlertStock) : "0",
        Bonus: productFormData.Bonus !== undefined ? String(productFormData.Bonus) : "0"
      };

      productDepot.map((el) => {
        productObject.Disponible += parseFloat(el.Disponible);
      });

      // validation
      if (!selectedCategory || !selectedCategory?.Name) {
        setIsSubmitting(false);
        return showToast("error", `Sélectionner votre category`);
      }

      await addNewProductApi(productObject);
      showToast("success", "Produit ajoutée");
      closePopup("Close");
    } catch (err) {
      console.log(err);
      showToast("error", `${err?.response?.data?.error}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditeProduct = async () => {
    if (isSubmitting) return; // Prevent duplicate submissions
    
    try {
      setIsSubmitting(true);
      
      // Create product object with only product info (exclude Stock and Disponible)
      const productObject = {
        Code: productFormData.Code,
        Name: productFormData.Name,
        Unity: productFormData.Unity,
        Packaging: productFormData.Packaging,
        isActive: productFormData.isActive,
        Price: productFormData.Price,
        HaveTVA: productFormData.HaveTVA,
        PriceTTC: productFormData.PriceTTC,
        Remise: productFormData.Remise,
        Categorie: productFormData.Categorie,
        CategorieID: productFormData.CategorieID,
        CodeProduitComptable: productFormData.CodeProduitComptable,
        FOURNISSEUR: productFormData.FOURNISSEUR,
        FournisseurID: productFormData.FournisseurID,
        ExperationDate: productFormData.ExperationDate,
        LotNumber: productFormData.LotNumber,
        AlertStock: productFormData.AlertStock !== undefined ? String(productFormData.AlertStock) : "0",
        Bonus: productFormData.Bonus !== undefined ? String(productFormData.Bonus) : "0"
      };
      // Do NOT include Stock and Disponible - backend will preserve original values

      // validation
      if (productFormData.Categorie === "") {
        setIsSubmitting(false);
        return showToast("error", `Sélectionner votre category`);
      }

      await updateProductApi(productObject, productID);
      showToast("success", "Produit modifiée");
      closePopup("Close");
    } catch (err) {
      showToast("error", `${err?.response?.data?.error}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDepotIputChange = (index, event) => {
    try {
      const { value } = event.target;
      const updatedDepotList = [...productDepot];
      updatedDepotList[index].Disponible = parseFloat(value);
      setProductDepot(updatedDepotList);
    } catch (err) {
      console.log(err);
    }
  };

  const hasDepotValueChanged = (index) => {
    if (!originalProductDepot[index] || !productDepot[index]) {
      return false;
    }
    // Match by City name to ensure we're comparing the same depot
    const currentDepot = productDepot[index];
    const originalDepot = originalProductDepot.find(depot => depot.City === currentDepot.City);
    
    if (!originalDepot) {
      return false;
    }
    
    const originalValue = parseFloat(originalDepot.Disponible || 0);
    const currentValue = parseFloat(currentDepot.Disponible || 0);
    return Math.abs(originalValue - currentValue) > 0.01; // Use small threshold for floating point comparison
  };

  const getDepotInputBorderClass = (index) => {
    // Only show border colors in edit mode when a specific lot number is selected
    if (clienFormAction !== "edite" || productFormData.selectedLotNumber === "all") {
      return "";
    }

    if (!originalProductDepot[index] || !productDepot[index]) {
      return "";
    }

    // Match by City name to ensure we're comparing the same depot
    const currentDepot = productDepot[index];
    const originalDepot = originalProductDepot.find(depot => depot.City === currentDepot.City);
    
    if (!originalDepot) {
      return "";
    }
    
    const originalValue = parseFloat(originalDepot.Disponible || 0);
    const currentValue = parseFloat(currentDepot.Disponible || 0);
    
    // If current value is less than original, show danger border
    if (currentValue < originalValue) {
      return "border-[#bb2124] border-2";
    }
    else if(currentValue > originalValue) {
    // Otherwise show success border
    return "border-[#22BB33] border-2";
    }
    else {
      return "";
    }
  };

  const formatDepotDisponible = (value) => {
    const parsedValue = parseFloat(value);
    if (isNaN(parsedValue)) return "0.00";
    return parsedValue.toFixed(2);
  };

  const handleUpdateSingleDepot = async (depotIndex) => {
    try {
      setIsLoading(true);
      const currentDepot = productDepot[depotIndex];
      const newDisponible = parseFloat(currentDepot.Disponible || 0);
      const selectedLotNumber = productFormData.selectedLotNumber;

      // Validate that a specific lot number is selected (not "all")
      if (!selectedLotNumber || selectedLotNumber === "all" || selectedLotNumber === "") {
        toast.error("Veuillez sélectionner un numéro de lot spécifique", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        });
        return;
      }

      // Call API to update single depot stock in StockEnter only
      await updateSingleDepotStockApi(
        productFormData.Code,
        currentDepot.City,
        newDisponible,
        currentDepot.fromDepot,
        selectedLotNumber
      );

      // Refresh from StockEnter for the selected lot number
      const productLotNumbers = await getAllStockEntryByLotNumberApi(productFormData.Code);
      const selectedLotNumberData = productLotNumbers.find(el => el.LotNumber === selectedLotNumber);
      
      if (selectedLotNumberData && selectedLotNumberData.ProductDetails && selectedLotNumberData.ProductDetails[0]) {
        const stockDetails = selectedLotNumberData.ProductDetails[0].Stock;
        
        if (stockDetails) {
          // Store original values for comparison
          const originalStock = stockDetails.map(depot => ({
            ...depot,
            Disponible: parseFloat(depot.Disponible || 0)
          }));
          setOriginalProductDepot(originalStock);
          setProductDepot(stockDetails);
        }
      }
      
      // Show success toast without closing the modal
      toast.success("Dépôt mis à jour", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
    } catch (err) {
      console.log(err);
      // Show error toast without closing the modal
      toast.error(`${err?.response?.data?.error || "Erreur lors de la mise à jour"}`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const hadleShowToast = (type, message) => {
    if (type === "success") {
      // setShowDepotModal(!showDepotModal);
      // toast.success(message, {
      //   position: "top-right",
      //   autoClose: 5000,
      //   hideProgressBar: false,
      //   closeOnClick: true,
      //   pauseOnHover: true,
      //   draggable: true,
      //   progress: undefined,
      //   theme: "colored",
      // });
    }
    if (type === "error") {
      // toast.error(message, {
      //   position: "top-right",
      //   autoClose: 5000,
      //   hideProgressBar: false,
      //   closeOnClick: true,
      //   pauseOnHover: true,
      //   draggable: true,
      //   progress: undefined,
      //   theme: "colored",
      // });
    }
  };

  return (
    <form
      style={{
        overflow: "scroll",
        height: "80vh",
        position: "relative",
      }}
    >
      <ToastContainer />
      {isLoading && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <Spinner className="h-12 w-12" />
        </div>
      )}
      <Dialog
        size="xl"
        open={showCategoryModal}
        handler={handleNewCategoryModal}
      >
        <DialogHeader>
          <p className="w-full text-center">Nouvelle catégorie</p>
        </DialogHeader>
        <DialogBody>
          <NewCategory closePopup={handleNewCategoryModal} />
        </DialogBody>
      </Dialog>

      <Dialog size="xl" open={showUnitModal} handler={handleNewUnitModal}>
        <DialogHeader>
          <p className="w-full text-center">Nouvelle unité</p>
        </DialogHeader>
        <DialogBody>
          <NewUnit closePopup={handleNewUnitModal} />
        </DialogBody>
      </Dialog>

      <Dialog size="xl" open={showDepotModal} handler={handleNewDepotModal}>
        <DialogHeader>
          <p className="w-full text-center">Nouvelle dépôt</p>
        </DialogHeader>
        <DialogBody>
          <NewDepot
            closePopup={handleNewDepotModal}
            showToast={hadleShowToast}
          />
        </DialogBody>
      </Dialog>

      <div className="mt-2 grid grid-cols-1 gap-5 md:grid-cols-3">
        <div>
          <label htmlFor="" className="text-md font-bold text-navy-700">
            Catégorie
          </label>
          <select
            label="select Version"
            className={`mt-2 flex w-full items-center justify-center rounded-xl border bg-white/0 p-2 text-sm outline-none`}
            name="CategorieID"
            value={productFormData.CategorieID}
            onChange={handleInputChange}
            disabled={clienFormAction === "show"}
          >
            <option value="">Séléctioner une catégorie</option>
            {productsCategories.map((el) => (
              <option key={el._id} value={el._id}>{el.Name}</option>
            ))}
          </select>
        </div>
        {clienFormAction !== "edite" && clienFormAction !== "show" && (
          <div>
            <Button
              variant="text"
              color="#236571"
              onClick={() => handleNewCategoryModal()}
              className="mt-7"
            >
              <span>Ajouter une Catégorie</span>
            </Button>
          </div>
        )}
      </div>

      <div className="mt-2 grid grid-cols-1 gap-5 md:grid-cols-3">
        <div>
          <label htmlFor="" className="text-md font-bold text-navy-700">
            Code du produit
          </label>
          <input
            type="text"
            id=""
            placeholder="Code du produit"
            className={`flex w-full items-center justify-center rounded-xl border bg-white/0 p-2 text-sm outline-none`}
            disabled={clienFormAction === "show"}
            name="Code"
            value={productFormData.Code}
            onChange={handleInputChange}
          />
        </div>

        <div>
          <label htmlFor="" className="text-md font-bold text-navy-700">
            Produit
          </label>
          <input
            type="text"
            id=""
            placeholder="Produit"
            className={`flex w-full items-center justify-center rounded-xl border bg-white/0 p-2 text-sm outline-none`}
            disabled={clienFormAction === "show"}
            name="Name"
            value={productFormData.Name}
            onChange={handleInputChange}
          />
        </div>

        <div>
          <label htmlFor="" className="text-md font-bold text-navy-700">
            Unité
          </label>
          <select
            label="select Version"
            className={`flex w-full items-center justify-center rounded-xl border bg-white/0 p-2 text-sm outline-none`}
            name="Unity"
            value={productFormData.Unity}
            onChange={handleInputChange}
            disabled={clienFormAction === "show"}
          >
            <option disabled value="">Séléctioner l'unité</option>
            {productFormData.Unity && !productUnits.some((el) => el.Name === productFormData.Unity) && (
              <option value={productFormData.Unity}>{productFormData.Unity}</option>
            )}
            {productUnits.map((el) => (
              <option key={el._id} value={el.Name}>{el.Name}</option>
            ))}
          </select>
        </div>
      </div>

      {clienFormAction !== "edite" && clienFormAction !== "show" && (
        <div className="mt-2">
          <Button
            variant="text"
            color="#236571"
            onClick={() => handleNewUnitModal()}
          >
            <span>Ajouter une unité</span>
          </Button>
        </div>
      )}

      <div className="mt-2 grid grid-cols-1 gap-5 md:grid-cols-3">
        <div>
          <label htmlFor="" className="text-md font-bold text-navy-700">
            Emballage
          </label>
          <input
            type="number"
            id=""
            placeholder="Emballage"
            className={`flex w-full items-center justify-center rounded-xl border bg-white/0 p-2 text-sm outline-none`}
            disabled={clienFormAction === "show"}
            name="Packaging"
            value={productFormData.Packaging}
            onChange={handleInputChange}
          />
        </div>

        <div>
          <label htmlFor="" className="text-md font-bold text-navy-700">
            Prix HT : en DH
          </label>
          <input
            type="number"
            id=""
            placeholder="Prix HT : en DH"
            className={`flex w-full items-center justify-center rounded-xl border bg-white/0 p-2 text-sm outline-none`}
            disabled={clienFormAction === "show"}
            name="Price"
            value={productFormData.Price}
            onChange={handleInputChange}
          />
        </div>
        <div>
          <label htmlFor="" className="text-md font-bold text-navy-700">
            TVA %
          </label>
          <input
            type="number"
            id=""
            placeholder="TVA %"
            className={`flex w-full items-center justify-center rounded-xl border bg-white/0 p-2 text-sm outline-none`}
            disabled={clienFormAction === "show"}
            name="HaveTVA"
            value={productFormData.HaveTVA}
            onChange={handleInputChange}
          />
        </div>
      </div>

      <div className="mt-2 grid grid-cols-1 gap-5 md:grid-cols-3">
        <div>
          <label htmlFor="" className="text-md font-bold text-navy-700">
            Prix TTC
          </label>
          <input
            type="number"
            id=""
            placeholder="Prix TTC"
            className={`flex w-full items-center justify-center rounded-xl border bg-white/0 p-2 text-sm outline-none`}
            disabled={clienFormAction === "show"}
            name="PriceTTC"
            value={productFormData.PriceTTC}
            onChange={handleInputChange}
          />
        </div>
        <div>
          <label htmlFor="" className="text-md font-bold text-navy-700">
            Remise %
          </label>
          <input
            type="number"
            id=""
            placeholder="Remise %"
            className={`flex w-full items-center justify-center rounded-xl border bg-white/0 p-2 text-sm outline-none`}
            disabled={clienFormAction === "show"}
            name="Remise"
            value={productFormData.Remise}
            onChange={handleInputChange}
          />
        </div>
        <div>
          <label htmlFor="" className="text-md font-bold text-navy-700">
            Code comptable de produit
          </label>
          <input
            type="text"
            id=""
            placeholder="Code comptable de produit"
            className={`flex w-full items-center justify-center rounded-xl border bg-white/0 p-2 text-sm outline-none`}
            disabled={clienFormAction === "show"}
            name="CodeProduitComptable"
            value={productFormData.CodeProduitComptable}
            onChange={handleInputChange}
          />
        </div>
        <div>
          <label htmlFor="" className="text-md font-bold text-navy-700">
            Fournisseur
          </label>
          <Combobox
            value={productFormData.FournisseurID}
            onChange={handleSelectedFournisseur}
            disabled={clienFormAction === "show"}
          >
            <div className="relative">
              <Combobox.Input
                placeholder="Fournisseur"
                displayValue={(fournisseurID) => getFournisseurDisplayName(fournisseurID)}
                onChange={(event) => setFournisseurQuery(event.target.value)}
                className="flex w-full items-center justify-center rounded-xl border bg-white/0 p-2 text-sm outline-none"
              />
              <Combobox.Options className="absolute z-10 max-h-32 w-full overflow-y-scroll rounded-xl border bg-white">
                {filteredFournisseurs.map((el) => (
                  <Combobox.Option
                    key={el._id}
                    value={el._id}
                    className="cursor-pointer rounded-xl p-2 hover:bg-gray-100"
                  >
                    {el.Name}
                  </Combobox.Option>
                ))}
              </Combobox.Options>
            </div>
          </Combobox>
        </div>
      </div>

      <div className="mt-2 grid grid-cols-1 gap-5 md:grid-cols-3">
        <div>
          <label htmlFor="" className="text-md font-bold text-navy-700">
            Date expiration
          </label>
          <input
            type="date"
            id=""
            placeholder="Date expiration"
            className={`flex w-full items-center justify-center rounded-xl border bg-white/0 p-2 text-sm outline-none`}
            disabled={clienFormAction === "show"}
            name="ExperationDate"
            value={
              clienFormAction === "add"
                ? productFormData.ExperationDate
                : productFormData?.ExperationDate?.split("T")[0]
            }
            onChange={handleInputChange}
          />
        </div>
        {/* <div>
          <label htmlFor="" className="text-md font-bold text-navy-700">
            Numéro de lot
          </label>

          <select
            label="select Version"
            className={`flex w-full items-center justify-center rounded-xl border bg-white/0 p-2 text-sm outline-none`}
            disabled={clienFormAction === "show"}
            name="LotNumber"
            value={productFormData.LotNumber}
            onChange={handleInputChange}
          >
            <option>Séléctionner le numéro de lot</option>
            <option>Numéro de lot 1</option>
            <option>Numéro de lot 2</option>
            <option>Numéro de lot 3</option>
          </select>
          <input
            type="text"
            id=""
            placeholder="Numéro de lot"
            className={`flex w-full items-center justify-center rounded-xl border bg-white/0 p-2 text-sm outline-none`}
            disabled={clienFormAction === "show"}
            name="LotNumber"
            value={productFormData.LotNumber}
            onChange={handleInputChange}
          />
        </div> */}

        <div>
          <label htmlFor="" className="text-md font-bold text-navy-700">
            Stock d'alerte
          </label>
          <input
            type="number"
            min="0"
            id=""
            placeholder="Stock d'alerte"
            className={`flex w-full items-center justify-center rounded-xl border bg-white/0 p-2 text-sm outline-none`}
            disabled={clienFormAction === "show"}
            name="AlertStock"
            value={productFormData.AlertStock}
            onChange={handleInputChange}
          />
        </div>

        <div>
          <label htmlFor="" className="text-md font-bold text-navy-700">
            Bonus
          </label>
          <input
            type="number"
            min="0"
            id=""
            placeholder="Bonus"
            className={`flex w-full items-center justify-center rounded-xl border bg-white/0 p-2 text-sm outline-none`}
            disabled={clienFormAction === "show"}
            name="Bonus"
            value={productFormData.Bonus}
            onChange={handleInputChange}
          />
        </div>
      </div>

      {(clienFormAction === "edite" || clienFormAction === "show") && (
        <>
          <div className="mt-5">
            <label htmlFor="" className="text-md font-bold text-navy-700">
              Numéro de lot
            </label>
            <select
              label="select Version"
              className={`mt-2 flex w-full items-center justify-center rounded-xl border bg-white/0 p-2 text-sm outline-none`}
              name="selectedLotNumber"
              value={productFormData.selectedLotNumber}
              onChange={handleInputChange}
            >
              <option value="" disabled>
                Sélectionner le numéro de lot
              </option>
              <option value="all">Tous</option>
              {lotNumbersData.map((lot, index) => {
                const label = `${lot.LotNumber}${
                  lot.ExperationDate ? ` - ${formatLotExpirationDate(lot.ExperationDate)}` : ""
                }`;
                return (
                  <option key={index} value={lot.LotNumber}>
                    {label}
                  </option>
                );
              })}
            </select>
          </div>

          {productDepot.length > 0 && (
            <>
              <p className="mt-5 text-xl font-bold text-navy-700">Dépôt</p>

              <div className="mt-2 grid grid-cols-1 gap-5 md:grid-cols-5">
                {productDepot.map((el, index) => (
                  <div key={index}>
                    <label htmlFor="" className="text-md font-bold text-navy-700">
                      {el.City}
                    </label>
                    {clienFormAction === "show" ? (
                      <div className="flex w-full items-center justify-between rounded-xl border bg-white/0 p-2 text-sm text-navy-700">
                        {formatDepotDisponible(el.Disponible)}
                      </div>
                    ) : (
                      <>
                        <input
                          type="number"
                          min="0"
                          id=""
                          className={`flex w-full items-center justify-center rounded-xl border bg-white/0 p-2 text-sm outline-none ${getDepotInputBorderClass(
                            index
                          )}`}
                          disabled={productFormData.selectedLotNumber === "all"}
                          value={formatDepotDisponible(el.Disponible)}
                          onChange={(event) => handleDepotIputChange(index, event)}
                        />
                        {productFormData.selectedLotNumber !== "all" &&
                          hasDepotValueChanged(index) && (
                            <Button
                              variant="gradient"
                              color="#236571"
                              size="sm"
                              className="mt-2 w-full"
                              onClick={() => handleUpdateSingleDepot(index)}
                              disabled={isLoading}
                            >
                              <span>Mettre à jour</span>
                            </Button>
                          )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {clienFormAction === "edite" && (
        <div className="mt-5">
          <Button
            variant="gradient"
            color="#236571"
            onClick={() => handleEditeProduct()}
            className="mr-1"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <div className="flex items-center gap-2">
                <Spinner className="h-4 w-4" />
                <span>Modification en cours...</span>
              </div>
            ) : (
            <span>Modifier</span>
            )}
          </Button>
          <Button
            variant="text"
            color="#cf503f"
            onClick={() => handleCancelOp()}
            className="mr-1"
            disabled={isSubmitting}
          >
            <span>Annuler</span>
          </Button>
        </div>
      )}

      {clienFormAction === "add" && (
        <div className="mt-5">
          <Button
            variant="gradient"
            color="#236571"
            onClick={() => handleAddNewProduct()}
            className="mr-1"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <div className="flex items-center gap-2">
                <Spinner className="h-4 w-4" />
                <span>Ajout en cours...</span>
              </div>
            ) : (
            <span>Ajouter</span>
            )}
          </Button>
          <Button
            variant="text"
            color="#cf503f"
            onClick={() => handleCancelOp()}
            className="mr-1"
            disabled={isSubmitting}
          >
            <span>Annuler</span>
          </Button>
        </div>
      )}
    </form>
  );
}

export default ProductForm;
